const DAY_MS = 86_400_000;

/** Whole days between `then` and now, floored (so "today" is 0). */
export function ageInDays(postedAt: number, now = Date.now()): number {
  return Math.max(0, Math.floor((now - postedAt) / DAY_MS));
}

/** Compact relative age for dense readouts: "12m", "4h", "3d", "5w". */
export function shortAge(postedAt: number, now = Date.now()): string {
  const ms = Math.max(0, now - postedAt);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(ms / DAY_MS);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

/**
 * Age with the unit spelled out: "13 hrs", "13 days", "1 day".
 *
 * Separate from `shortAge` rather than replacing it — the compact form still
 * earns its place in readouts measured in single characters, where "3d" fits and
 * "3 days" does not. This is for the one place with room to be read rather than
 * decoded: the badge over the listing photo.
 */
export function spelledAge(postedAt: number, now = Date.now()): string {
  const ms = Math.max(0, now - postedAt);

  // `plural` already emits the count — "4 hrs", not "hrs". Prefixing it with the
  // number again is what produced badges reading "4 4 HRS".
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return plural(minutes, "min");
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24) return plural(hours, "hr");
  const days = Math.floor(ms / DAY_MS);
  if (days < 14) return plural(days, "day");
  if (days < 60) return plural(Math.floor(days / 7), "week");
  return plural(Math.floor(days / 30), "month");
}

/**
 * True when a title says nothing the card has not already said in its stats row.
 *
 * Sources routinely title a unit by its own layout — "1 Bedroom A", "Studio",
 * "2 Bed 1 Bath" — which lands directly under a row already reading "1 bd · 1 ba"
 * and costs a line to repeat it. Anything with real words in it ("Charming
 * Victorian, parking") is kept.
 *
 * Works by deletion rather than by matching a shape: strip the layout vocabulary
 * and the punctuation, and judge what survives. A single leftover letter is a
 * unit designator — the "A" in "1 Bedroom A" — not a description.
 */
export function isLayoutOnlyTitle(title: string): boolean {
  const rest = title
    .toLowerCase()
    .replace(/\b\d+(\.\d+)?\s*(bed|beds|bedrooms?|bd|br|bath|baths|bathrooms?|ba)\b/g, "")
    .replace(/\b(studio|unit|apt|apartment)\b/g, "")
    .replace(/[^a-z]/g, "");
  return rest.length <= 1;
}

/** shortAge with a trailing "ago", minus the "just now ago" awkwardness. */
export function agoLabel(ts: number, now = Date.now()): string {
  const s = shortAge(ts, now);
  return s === "just now" ? s : `${s} ago`;
}

export function fullDate(ts: number): string {
  return new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "$3,450" — full precision, for the popup. */
export function money(price: number): string {
  return `$${price.toLocaleString("en-US")}`;
}

/** "$3.4k" — for map pills, where every character costs space. */
export function moneyCompact(price: number): string {
  if (price < 1000) return `$${price}`;
  const k = price / 1000;
  return `$${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

export function bedLabel(beds: number | null): string {
  if (beds == null) return "— bd";
  if (beds < 0.5) return "Studio";
  return `${beds % 1 === 0 ? beds : beds.toFixed(1)} bd`;
}

export function bathLabel(baths: number | null): string {
  return baths == null ? "— ba" : `${baths % 1 === 0 ? baths : baths.toFixed(1)} ba`;
}

/** "1.2 km" / "800 m" — the radius readout. */
export function distanceLabel(meters: number): string {
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
  return `${(meters / 1000).toFixed(meters < 10_000 ? 2 : 1)} km`;
}

/** Source keys are lowercase slugs; these are how people say them out loud. */
const SOURCE_NAMES: Record<string, string> = {
  craigslist: "Craigslist",
  zumper: "Zumper",
  redfin: "Redfin",
  rentcast: "RentCast",
  rentsfnow: "RentSFNow",
  homeharvest: "Realtor.com",
  dahlia: "DAHLIA",
  zillow: "Zillow",
  apartments: "Apartments.com",
  reddit: "Reddit",
};

export function sourceName(source: string): string {
  return SOURCE_NAMES[source] ?? source.charAt(0).toUpperCase() + source.slice(1);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}
